import { useCallback, useMemo } from 'react';

import {
  parseDateRangeFilterValue,
  serializeDateRangeFilterValue,
} from '@/components/data-table/filters-v2/utils/date-range-filter-value';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
  detectPresetFromDates,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { DateRangePickerV2Filter } from '@/types/filters';

/**
 * Serializes DateRangeV2 to a URL-safe string: "YYYY-MM-DD_YYYY-MM-DD".
 * Thin aliases over the app-wide date-range codec (filters-v2 utils) so the
 * v1 finance pages and the v2 planner filter can never drift apart.
 */
export const serializeDateRange = (dates: DateRangeV2): string =>
  serializeDateRangeFilterValue(dates);

/** Parses the URL param value back to DateRangeV2 (null on malformed/inverted input) */
export const parseDateRange = (value: string): DateRangeV2 | null =>
  parseDateRangeFilterValue(value);

interface Props {
  config: DateRangePickerV2Filter;
  value: string;
  onChange: (key: string, value: string) => void;
}

export const FilterDateRangePickerV2 = ({ config, value, onChange }: Props) => {
  const { dates, preset } = useMemo(() => {
    const parsed = parseDateRange(value);
    if (!parsed) return { dates: null, preset: null };
    return { dates: parsed, preset: detectPresetFromDates(parsed) };
  }, [value]);

  const handleChange = useCallback(
    (newDates: DateRangeV2) => {
      onChange(config.name, serializeDateRange(newDates));
    },
    [config.name, onChange]
  );

  const handlePresetSelect = useCallback(
    (newPreset: DateRangePreset) => {
      const presetDates = getDateRangeFromPreset(newPreset);
      onChange(config.name, serializeDateRange(presetDates));
    },
    [config.name, onChange]
  );

  const handleReset = useCallback(() => {
    onChange(config.name, '');
  }, [config.name, onChange]);

  return (
    <DateRangePickerV2
      dates={dates}
      onChange={handleChange}
      selectedPreset={preset}
      onPresetSelect={handlePresetSelect}
      onReset={handleReset}
      defaultPrefix={config.label}
    />
  );
};
